import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useAtom } from 'jotai';
import { View, Text, Pressable, ActivityIndicator } from '../../lib/react-native-compat';
import { jobOpenCommitmentsAtom, userNameAtom } from '../../store/company';
import { uploadFieldReportPhoto, deleteFieldReportPhoto } from '../../db/company';
import { showToast } from '../../helpers/toast';
import { fieldReportAtom, fieldReportDirtyAtom } from './state';
import { sectionByKey } from './sectionDefs';
import AutoGrowTextarea from './AutoGrowTextarea';
import Lightbox from './Lightbox';
import { downscale } from './PhotosStep';

// Material Deliveries editor: one card per delivery instead of a table row.
// An optional material-type filter (JDE purchasing report code 1 description)
// narrows the vendor and PO lists before picking; the vendor dropdown is a
// type-to-search combobox over the job's open PO vendors (free text is still
// allowed); PO # suggestions then narrow to that vendor's POs. A vendor with
// exactly one PO auto-fills the PO #. The PO description shown under the PO #
// comes from the PO's largest-dollar line (PO_Line_Description + _2). Once a
// PO # matches, a one-line insight shows the PO's committed / received / open
// dollars and the placed + promised dates rolled up from its lines.
//
// layout="table" renders the same editor as Excel-style gridline rows for the
// sheet view (vendor/PO suggestions move into per-row datalists); the default
// card layout stays for the guided wizard.
//
// Delivery tickets (packing slips, BOLs — photo or PDF) upload per delivery
// through the photo pipeline with docType 'deliveryTicket', which keeps them
// off the Photos step; the row remembers its tickets via ticketPhotoIds.

const labelStyle = {
    fontSize: 10,
    fontWeight: 600,
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 3,
    display: 'block',
};

const CARD_FIELD_HEIGHT = 34;

const inputStyle = {
    width: '100%',
    // Match the AutoGrowTextarea's min-height so the Supplier / PO # /
    // What Was Delivered boxes line up in the same grid row.
    height: CARD_FIELD_HEIGHT,
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    background: '#fff',
    fontSize: 13,
    color: '#0F172A',
    padding: '6px 9px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    outline: 'none',
};

// Sheet-view table styles — mirror SheetView's gridline constants so the
// section reads as one Excel sheet with the others.
const SHEET_BORDER = '1px solid #CBD5E1';
const sheetTh = {
    border: SHEET_BORDER,
    background: '#F1F5F9',
    padding: '6px 8px',
    fontSize: 11,
    fontWeight: 700,
    color: '#334155',
    textAlign: 'left',
    whiteSpace: 'nowrap',
};
const sheetTd = { border: SHEET_BORDER, padding: 0, verticalAlign: 'top', background: '#fff' };
const sheetInput = {
    width: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 13,
    color: '#0F172A',
    padding: '7px 8px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
};

const norm = (v) => String(v ?? '').trim().toLowerCase();
const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};
const fMoney = (n) => {
    const v = Math.abs(n);
    const sign = n < 0 ? '-' : '';
    if (v >= 1e6) return `${sign}$${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e4) return `${sign}$${Math.round(v / 1e3).toLocaleString('en-US')}K`;
    return `${sign}$${Math.round(v).toLocaleString('en-US')}`;
};
const fDate = (d) => (d ? moment.utc(d).format('M/D/YY') : '');
const amt = (r) => {
    for (const k of ['Original_Line_Amount', 'PO_Ordered_Amount', 'PO_Open_Amount']) {
        const n = parseFloat(r?.[k]);
        if (Number.isFinite(n) && n !== 0) return Math.abs(n);
    }
    return 0;
};

// Type-to-search single-select dropdown. Typing filters; anything typed is
// kept as free text, so vendors outside the PO list still work.
const VendorCombo = ({ value, options, disabled, placeholder, onPick, onText }) => {
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);
    useEffect(() => {
        const onDown = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, []);

    const q = norm(value);
    const filtered = options.filter((v) => !q || norm(v).includes(q));
    return (
        <div ref={boxRef} style={{ position: 'relative' }}>
            <input
                type="text"
                value={value ?? ''}
                disabled={disabled}
                placeholder={placeholder}
                onFocus={() => setOpen(true)}
                onChange={(e) => { onText(e.target.value); setOpen(true); }}
                style={inputStyle}
            />
            {open && !disabled && filtered.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1px solid #CBD5E1', borderRadius: 8, marginTop: 2, maxHeight: 240, overflowY: 'auto', boxShadow: '0 8px 20px rgba(15,23,42,0.12)' }}>
                    {filtered.map((v) => (
                        <div
                            key={v}
                            onMouseDown={(e) => { e.preventDefault(); onPick(v); setOpen(false); }}
                            style={{ padding: '8px 10px', fontSize: 13, color: '#0F172A', cursor: 'pointer', borderBottom: '1px solid #F1F5F9', background: norm(v) === q ? '#EFF6FF' : '#fff' }}
                        >
                            {v}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const MaterialDeliveryCards = ({ readOnly, layout = 'cards' }) => {
    const [report, setReport] = useAtom(fieldReportAtom);
    const [, setDirty] = useAtom(fieldReportDirtyAtom);
    const [openCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [userName] = useAtom(userNameAtom);
    const def = sectionByKey.materials;

    // Delivery ticket uploads: one hidden file input shared by all rows;
    // pickRowRef holds the rowId the picker was opened for.
    const fileRef = useRef(null);
    const pickRowRef = useRef(null);
    // pendingTickets: [{ id, rowId, file, previewUrl, uploading, failed }]
    const [pendingTickets, setPendingTickets] = useState([]);
    // lightbox: { src, title } — image tickets open in-page instead of a new tab.
    const [lightbox, setLightbox] = useState(null);
    const photosById = useMemo(() => Object.fromEntries(
        (report?.photos || []).map((p) => [p.photoId, p]),
    ), [report?.photos]);

    // Per-PO rollup from the job's open commitment lines: owning vendor, the
    // largest-dollar line's description, the material types on the PO, and the
    // insight totals — ordered / received / open dollars plus placed and
    // promised dates (earliest order date, latest promised shipment).
    const poInfo = useMemo(() => {
        const map = {};
        for (const r of openCommitments || []) {
            const po = String(r?.PO_Number ?? '').trim();
            if (!po) continue;
            if (!map[po]) {
                map[po] = {
                    vendor: '', bestAmt: -1, desc: '', types: new Set(),
                    ordered: 0, received: 0, open: 0, lines: 0, orderDate: '', promised: '',
                };
            }
            const info = map[po];
            const vendor = String(r?.Supplier_AB_Name ?? '').trim();
            if (vendor) info.vendor = vendor;
            const type = String(r?.Purchasing_Reporting_Code_1_Description ?? '').trim();
            if (type) info.types.add(type);
            // Ordered falls back to open+received (mirrors thirdPartyCostDrivers).
            info.ordered += num(r?.PO_Ordered_Amount)
                || (num(r?.PO_True_Open_Amount) + num(r?.PO_Received_Amount));
            info.received += num(r?.PO_Received_Amount);
            info.open += r?.PO_True_Open_Amount != null
                ? num(r.PO_True_Open_Amount) : num(r?.PO_Open_Amount);
            info.lines += 1;
            const od = String(r?.Order_Date ?? '').trim();
            if (od && (!info.orderDate || od < info.orderDate)) info.orderDate = od;
            const pd = String(r?.Promised_Shipment_Date ?? '').trim();
            if (pd && (!info.promised || pd > info.promised)) info.promised = pd;
            // PO description = the largest-dollar line's PO_Line_Description
            // + PO_Line_Description_2, whether the PO has 1 line or 40.
            const a = amt(r);
            if (a > info.bestAmt) {
                info.bestAmt = a;
                info.desc = [r?.PO_Line_Description, r?.PO_Line_Description_2]
                    .map((d) => String(d ?? '').trim()).filter(Boolean).join(' ')
                    || String(r?.Account_Description ?? '').trim();
            }
        }
        return map;
    }, [openCommitments]);

    const materialTypes = useMemo(() => [...new Set(
        Object.values(poInfo).flatMap((i) => [...i.types]),
    )].sort(), [poInfo]);

    if (!report) return null;
    const rows = report.sections?.materials || [];

    const setRows = (next) => {
        setReport({ ...report, sections: { ...report.sections, materials: next } });
        setDirty(true);
    };

    const updateRow = (i, patch) => {
        const next = rows.slice();
        next[i] = { ...next[i], ...patch };
        setRows(next);
    };
    const addRow = () => setRows([...rows, {}]);
    const delRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

    // POs matching a card's material-type filter (all POs when unfiltered).
    const posForType = (type) => Object.keys(poInfo)
        .filter((po) => !type || poInfo[po].types.has(type)).sort();
    const vendorsForType = (type) => [...new Set(
        posForType(type).map((po) => poInfo[po].vendor).filter(Boolean),
    )].sort();

    const matchVendor = (type, name) => vendorsForType(type).find((v) => norm(v) === norm(name));

    const setSupplier = (i, type, value) => {
        const patch = { supplier: value };
        const hit = matchVendor(type, value);
        if (hit) {
            const pos = posForType(type).filter((po) => poInfo[po].vendor === hit);
            if (pos.length === 1 && !rows[i]?.poNumber) patch.poNumber = pos[0];
        }
        updateRow(i, patch);
    };

    // ---- Delivery tickets ----
    // Rows get a stable rowId the first time a ticket is picked for them, so
    // an upload finishing after rows were added/removed still lands on the
    // right delivery.
    const ensureRowId = (i) => {
        if (rows[i]?.rowId) return rows[i].rowId;
        const id = `d-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        updateRow(i, { rowId: id });
        return id;
    };

    const uploadTicket = async (item) => {
        setPendingTickets((p) => p.map((x) => (x.id === item.id ? { ...x, uploading: true, failed: false } : x)));
        try {
            const isPdf = item.file.type === 'application/pdf';
            const blob = isPdf ? item.file : await downscale(item.file);
            const res = await uploadFieldReportPhoto({
                reportId: report.reportId,
                buId: report.buId,
                caption: 'Delivery ticket',
                location: item.file.name, // docs: Location = original file name
                uploadedBy: userName,
                contentType: isPdf ? 'application/pdf' : 'image/jpeg',
                blob,
                docType: 'deliveryTicket',
            });
            if (!res?.PhotoID) throw new Error(res?.error || 'Upload failed');
            setPendingTickets((p) => p.filter((x) => x.id !== item.id));
            setReport((r) => ({
                ...r,
                photos: [...(r.photos || []), {
                    photoId: res.PhotoID,
                    sasUrl: res.sasUrl || item.previewUrl,
                    docType: 'deliveryTicket',
                    contentType: isPdf ? 'application/pdf' : 'image/jpeg',
                    caption: 'Delivery ticket',
                    location: item.file.name,
                }],
                sections: {
                    ...r.sections,
                    materials: (r.sections?.materials || []).map((row) => (
                        row.rowId === item.rowId
                            ? { ...row, ticketPhotoIds: [...(row.ticketPhotoIds || []), res.PhotoID] }
                            : row
                    )),
                },
            }));
            setDirty(true);
        } catch (err) {
            setPendingTickets((p) => p.map((x) => (x.id === item.id ? { ...x, uploading: false, failed: true } : x)));
            showToast('Ticket upload failed — check connection and tap Retry.', 'error');
        }
    };

    const pickTickets = (i) => {
        pickRowRef.current = ensureRowId(i);
        if (fileRef.current) fileRef.current.click();
    };

    // Picked files upload right away, like the sub-doc uploads.
    const onPickTickets = (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        const rowId = pickRowRef.current;
        if (!rowId) return;
        const items = files.map((file) => ({
            id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            rowId,
            file,
            previewUrl: file.type === 'application/pdf' ? null : URL.createObjectURL(file),
            uploading: true,
            failed: false,
        }));
        setPendingTickets((p) => [...p, ...items]);
        items.forEach((item) => uploadTicket(item));
    };

    const discardPendingTicket = (item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        setPendingTickets((p) => p.filter((x) => x.id !== item.id));
    };

    const deleteTicket = async (i, photoId) => {
        try {
            await deleteFieldReportPhoto({ photoId, updatedBy: userName });
            setReport((r) => ({
                ...r,
                photos: (r.photos || []).filter((p) => p.photoId !== photoId),
                sections: {
                    ...r.sections,
                    materials: (r.sections?.materials || []).map((row, idx) => (
                        idx === i
                            ? { ...row, ticketPhotoIds: (row.ticketPhotoIds || []).filter((id) => id !== photoId) }
                            : row
                    )),
                },
            }));
            setDirty(true);
        } catch (err) {
            showToast('Ticket delete failed — try again.', 'error');
        }
    };

    // Thumbnail strip: uploaded tickets (click to open), in-flight/failed
    // chips, and the add button. compact = the sheet view's table cells; the
    // card layout sits it inline to the right of What Was Delivered, so it
    // stays on one line with the field instead of adding a row.
    const ticketStrip = (r, i, compact) => {
        const ids = r.ticketPhotoIds || [];
        const pend = pendingTickets.filter((t) => t.rowId && t.rowId === r.rowId);
        if (readOnly && ids.length === 0) return null;
        const size = compact ? 48 : 56;
        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 0, padding: compact ? '0 8px 6px' : 0, flexShrink: 0, maxWidth: compact ? undefined : '55%' }}>
                {ids.map((id) => {
                    const p = photosById[id];
                    if (!p) return null;
                    const isPdf = String(p.contentType || '').includes('pdf') || /\.pdf$/i.test(p.location || '');
                    return (
                        <div key={id} style={{ position: 'relative' }}>
                            {p.sasUrl && !isPdf ? (
                                // Images open the in-page lightbox; PDFs (below) still open a tab.
                                <img
                                    src={p.sasUrl}
                                    alt=""
                                    title={p.location || 'Delivery ticket'}
                                    onClick={() => setLightbox({ src: p.sasUrl, title: p.location || 'Delivery ticket' })}
                                    style={{ width: size, height: size, objectFit: 'cover', borderRadius: 6, display: 'block', border: '1px solid #E2E8F0', cursor: 'pointer' }}
                                />
                            ) : (
                                <a href={p.sasUrl || undefined} target="_blank" rel="noopener noreferrer" title={p.location || 'Delivery ticket'} style={{ display: 'block', textDecoration: 'none' }}>
                                    <div style={{ width: size, height: size, borderRadius: 6, background: '#F1F5F9', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#64748B' }}>
                                        {isPdf ? 'PDF' : 'IMG'}
                                    </div>
                                </a>
                            )}
                            {!readOnly && (
                                <button
                                    onClick={() => deleteTicket(i, id)}
                                    title="Remove ticket"
                                    style={{ position: 'absolute', top: -6, right: -6, width: 16, height: 16, borderRadius: 8, border: '1px solid #E2E8F0', background: '#fff', color: '#DC2626', fontSize: 10, lineHeight: 1, cursor: 'pointer', padding: 0 }}
                                >×</button>
                            )}
                        </div>
                    );
                })}
                {pend.map((t) => (
                    t.uploading ? (
                        <div key={t.id} style={{ width: size, height: size, borderRadius: 6, background: '#F8FAFC', border: '1px dashed #CBD5E1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ActivityIndicator size="small" />
                        </div>
                    ) : (
                        <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, height: size, borderRadius: 6, background: '#FEF2F2', border: '1px dashed #FCA5A5', padding: '0 8px' }}>
                            <span style={{ fontSize: 10, fontWeight: 600, color: '#B91C1C' }}>Failed</span>
                            <button onClick={() => uploadTicket(t)} style={{ border: 'none', background: 'transparent', color: '#1D4ED8', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>Retry</button>
                            <button onClick={() => discardPendingTicket(t)} title="Discard" style={{ border: 'none', background: 'transparent', color: '#DC2626', fontSize: 12, lineHeight: 1, cursor: 'pointer', padding: 0 }}>×</button>
                        </div>
                    )
                ))}
                {!readOnly && (
                    <button
                        onClick={() => pickTickets(i)}
                        title="Delivery ticket / packing slip — photo or PDF"
                        style={{ border: '1px solid #BFDBFE', background: '#EFF6FF', color: '#1D4ED8', borderRadius: 6, fontSize: 11, fontWeight: 600, padding: compact ? '4px 7px' : '0 10px', height: compact ? undefined : CARD_FIELD_HEIGHT, whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: 'inherit' }}
                    >+ Delivery ticket</button>
                )}
            </div>
        );
    };

    const ticketFileInput = !readOnly ? (
        <input
            ref={fileRef}
            type="file"
            accept="application/pdf,image/*"
            multiple
            style={{ display: 'none' }}
            onChange={onPickTickets}
        />
    ) : null;

    // One-line PO insight rendered under the PO # once it matches a known PO:
    // committed vs received vs open dollars, then placed/promised dates and
    // line count. Skipped when the PO has no dollar data (e.g. free-typed #).
    const poInsightBlock = (r, compact) => {
        const info = poInfo[String(r.poNumber ?? '').trim()];
        if (!info || !(info.ordered || info.received || info.open)) return null;
        const pct = info.ordered > 0 ? Math.round((info.received / info.ordered) * 100) : null;
        const amounts = [
            `PO ${fMoney(info.ordered)}`,
            `Received ${fMoney(info.received)}${pct != null ? ` (${pct}%)` : ''}`,
            `Open ${fMoney(info.open)}`,
        ].join(' · ');
        const timing = [
            info.orderDate ? `Placed ${fDate(info.orderDate)}` : '',
            info.promised ? `Promised ${fDate(info.promised)}` : '',
            info.lines > 1 ? `${info.lines} lines` : '',
        ].filter(Boolean).join(' · ');
        return (
            <div style={{ fontSize: 11, lineHeight: 1.5, ...(compact ? { padding: '0 8px 5px' } : { marginTop: 3 }) }}>
                <span style={{ color: '#475569', fontWeight: 600 }}>{amounts}</span>
                {timing ? <span style={{ color: '#94A3B8' }}>{` · ${timing}`}</span> : null}
            </div>
        );
    };

    // Derived per-row PO context shared by both layouts.
    const rowCtx = (r) => {
        const type = String(r.materialType ?? '').trim();
        const isPO = (r.paymentType || 'PO') === 'PO';
        const hit = matchVendor(type, r.supplier);
        const poOptions = hit
            ? posForType(type).filter((po) => poInfo[po].vendor === hit)
            : posForType(type);
        const poDesc = poInfo[String(r.poNumber ?? '').trim()]?.desc || '';
        return { type, isPO, hit, poOptions, poDesc };
    };

    if (layout === 'table') {
        const hasTypes = materialTypes.length > 0;
        const colCount = 4 + (hasTypes ? 1 : 0) + (readOnly ? 0 : 1);
        const dashCell = (
            <div style={{ ...sheetInput, background: '#F8FAFC', color: '#94A3B8' }}>—</div>
        );
        return (
            <View>
                {ticketFileInput}
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={{ ...sheetTh, minWidth: 120 }}>Payment</th>
                                {hasTypes && <th style={{ ...sheetTh, minWidth: 140 }}>Material Type (filter)</th>}
                                <th style={{ ...sheetTh, minWidth: 170 }}>Supplier / Vendor</th>
                                <th style={{ ...sheetTh, minWidth: 130 }}>PO #</th>
                                <th style={{ ...sheetTh, minWidth: 240 }}>What Was Delivered</th>
                                {!readOnly && <th style={{ ...sheetTh, minWidth: 30, width: 30 }} />}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={colCount} style={{ ...sheetTd, background: '#F8FAFC' }}>
                                        <div style={{ ...sheetInput, color: '#94A3B8', fontStyle: 'italic' }}>
                                            {readOnly ? 'No entries.' : `No ${def.rowNoun} entries — add a row below.`}
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {rows.map((r, i) => {
                                const { type, isPO, hit, poOptions, poDesc } = rowCtx(r);
                                return (
                                    <tr key={i}>
                                        <td style={sheetTd}>
                                            {/* Datalists live inside a cell — tr allows only td/th children. */}
                                            <datalist id={`fr-matv-t-${i}`}>
                                                {vendorsForType(type).map((v) => <option key={v} value={v} />)}
                                            </datalist>
                                            <datalist id={`fr-po-t-${i}`}>
                                                {poOptions.map((po) => <option key={po} value={po}>{poInfo[po].desc || ''}</option>)}
                                            </datalist>
                                            <select
                                                value={r.paymentType || 'PO'}
                                                disabled={readOnly}
                                                onChange={(e) => updateRow(i, { paymentType: e.target.value, ...(e.target.value !== 'PO' ? { poNumber: '' } : null) })}
                                                style={{ ...sheetInput, appearance: 'auto', cursor: readOnly ? 'default' : 'pointer' }}
                                            >
                                                {['PO', 'Check Request', 'P-Card'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                                            </select>
                                        </td>
                                        {hasTypes && (
                                            <td style={sheetTd}>
                                                {isPO ? (
                                                    <select
                                                        value={type}
                                                        disabled={readOnly}
                                                        onChange={(e) => updateRow(i, { materialType: e.target.value })}
                                                        style={{ ...sheetInput, appearance: 'auto', cursor: readOnly ? 'default' : 'pointer', color: type ? '#0F172A' : '#94A3B8' }}
                                                    >
                                                        <option value="">All material types</option>
                                                        {materialTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                                                    </select>
                                                ) : dashCell}
                                            </td>
                                        )}
                                        <td style={sheetTd}>
                                            <input
                                                type="text"
                                                value={r.supplier ?? ''}
                                                disabled={readOnly}
                                                list={isPO ? `fr-matv-t-${i}` : undefined}
                                                placeholder={isPO ? 'Search PO vendors…' : 'Vendor name…'}
                                                onChange={(e) => (isPO ? setSupplier(i, type, e.target.value) : updateRow(i, { supplier: e.target.value }))}
                                                style={sheetInput}
                                            />
                                        </td>
                                        <td style={sheetTd}>
                                            {isPO ? (
                                                <div>
                                                    <input
                                                        type="text"
                                                        value={r.poNumber ?? ''}
                                                        disabled={readOnly}
                                                        list={`fr-po-t-${i}`}
                                                        placeholder={hit ? `${poOptions.length} PO${poOptions.length === 1 ? '' : 's'}…` : 'PO #…'}
                                                        onChange={(e) => updateRow(i, { poNumber: e.target.value })}
                                                        style={sheetInput}
                                                    />
                                                    {poDesc ? (
                                                        <div style={{ fontSize: 11, color: '#64748B', padding: '0 8px 5px' }}>{poDesc}</div>
                                                    ) : null}
                                                    {poInsightBlock(r, true)}
                                                </div>
                                            ) : dashCell}
                                        </td>
                                        <td style={sheetTd}>
                                            <AutoGrowTextarea
                                                value={r.material ?? ''}
                                                disabled={readOnly}
                                                placeholder="What was delivered…"
                                                onChange={(e) => updateRow(i, { material: e.target.value })}
                                                style={{ ...sheetInput, display: 'block' }}
                                            />
                                            {ticketStrip(r, i, true)}
                                        </td>
                                        {!readOnly && (
                                            <td style={{ ...sheetTd, textAlign: 'center', verticalAlign: 'middle' }}>
                                                <button
                                                    onClick={() => delRow(i)}
                                                    title="Remove delivery"
                                                    style={{ border: 'none', background: 'transparent', color: '#DC2626', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '6px 8px' }}
                                                >×</button>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {!readOnly && (
                    <Pressable onPress={addRow} style={{ alignSelf: 'flex-start', marginTop: 4, cursor: 'pointer', paddingTop: 4, paddingBottom: 4, paddingRight: 8 }}>
                        <Text style={{ fontSize: 12, fontWeight: '600', color: '#1D4ED8' }}>{`+ Add ${def.rowNoun}`}</Text>
                    </Pressable>
                )}
                {lightbox && <Lightbox src={lightbox.src} title={lightbox.title} onClose={() => setLightbox(null)} />}
            </View>
        );
    }

    return (
        <View>
            {ticketFileInput}
            {rows.length === 0 && (
                <View style={{ borderRadius: 12, border: '1px dashed #CBD5E1', padding: 24, alignItems: 'center', marginBottom: 12, backgroundColor: '#F8FAFC' }}>
                    <Text style={{ fontSize: 14, color: '#64748B', textAlign: 'center' }}>
                        {readOnly ? `No ${def.rowNoun} entries on this report.` : `Nothing here yet — add the first ${def.rowNoun} below, or tap Next if none today.`}
                    </Text>
                </View>
            )}

            {rows.map((r, i) => {
                const { type, isPO, hit, poOptions, poDesc } = rowCtx(r);
                return (
                    <div key={i} style={{ borderRadius: 12, border: '1px solid #E2E8F0', background: '#fff', padding: 12, marginBottom: 8 }}>
                        {/* Title + payment-type radios share one line; PO purchases pick
                            from the job's PO vendors and get a PO # box, while
                            Check Request / P-Card are free-form with no PO. */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                            <Text style={{ fontSize: 11, fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                {`Delivery ${i + 1}`}
                            </Text>
                            {['PO', 'Check Request', 'P-Card'].map((opt) => (
                                <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: '#0F172A', fontFamily: 'inherit', cursor: readOnly ? 'default' : 'pointer' }}>
                                    <input
                                        type="radio"
                                        name={`fr-pay-${i}`}
                                        checked={(r.paymentType || 'PO') === opt}
                                        disabled={readOnly}
                                        onChange={() => updateRow(i, { paymentType: opt, ...(opt !== 'PO' ? { poNumber: '' } : null) })}
                                        style={{ width: 15, height: 15, accentColor: '#1D4ED8', cursor: 'inherit' }}
                                    />
                                    {opt}
                                </label>
                            ))}
                            {!readOnly && (
                                <button
                                    onClick={() => delRow(i)}
                                    title="Remove delivery"
                                    style={{ marginLeft: 'auto', border: 'none', background: 'transparent', color: '#DC2626', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '2px 4px' }}
                                >×</button>
                            )}
                        </div>

                        <datalist id={`fr-po-${i}`}>
                            {poOptions.map((po) => <option key={po} value={po}>{poInfo[po].desc || ''}</option>)}
                        </datalist>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 8 }}>
                            {isPO && materialTypes.length > 0 && (
                                <div>
                                    <label style={labelStyle}>Material Type (filter)</label>
                                    <select
                                        value={type}
                                        disabled={readOnly}
                                        onChange={(e) => updateRow(i, { materialType: e.target.value })}
                                        style={{ ...inputStyle, cursor: readOnly ? 'default' : 'pointer', color: type ? '#0F172A' : '#94A3B8' }}
                                    >
                                        <option value="">All material types</option>
                                        {materialTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                            )}
                            <div>
                                <label style={labelStyle}>Supplier / Vendor</label>
                                {isPO ? (
                                    <VendorCombo
                                        value={r.supplier ?? ''}
                                        options={vendorsForType(type)}
                                        disabled={readOnly}
                                        placeholder="Search PO vendors…"
                                        onPick={(v) => setSupplier(i, type, v)}
                                        onText={(v) => updateRow(i, { supplier: v })}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={r.supplier ?? ''}
                                        disabled={readOnly}
                                        placeholder="Vendor name…"
                                        onChange={(e) => updateRow(i, { supplier: e.target.value })}
                                        style={inputStyle}
                                    />
                                )}
                            </div>
                            {isPO && (
                                <div>
                                    <label style={labelStyle}>PO #</label>
                                    <input
                                        type="text"
                                        value={r.poNumber ?? ''}
                                        disabled={readOnly}
                                        list={`fr-po-${i}`}
                                        placeholder={hit ? `${poOptions.length} PO${poOptions.length === 1 ? '' : 's'}…` : 'PO #…'}
                                        onChange={(e) => updateRow(i, { poNumber: e.target.value })}
                                        style={inputStyle}
                                    />
                                    {poDesc ? (
                                        <div style={{ fontSize: 11, color: '#64748B', marginTop: 3 }}>
                                            {poDesc}
                                        </div>
                                    ) : null}
                                    {poInsightBlock(r, false)}
                                </div>
                            )}
                            {/* Full-width row of its own — descriptions run long — with
                                the ticket strip riding alongside it. */}
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={labelStyle}>What Was Delivered</label>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <AutoGrowTextarea
                                            value={r.material ?? ''}
                                            disabled={readOnly}
                                            minHeight={CARD_FIELD_HEIGHT}
                                            placeholder="What was delivered…"
                                            onChange={(e) => updateRow(i, { material: e.target.value })}
                                            style={{ ...inputStyle, height: undefined, display: 'block' }}
                                        />
                                    </div>
                                    {ticketStrip(r, i, false)}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}

            {!readOnly && (
                <Pressable onPress={addRow} style={{ cursor: 'pointer', paddingTop: 4, paddingBottom: 4, marginBottom: 12, alignSelf: 'flex-start' }}>
                    <Text style={{ fontSize: 12, fontWeight: '600', color: '#1D4ED8' }}>+ Add delivery</Text>
                </Pressable>
            )}
            {lightbox && <Lightbox src={lightbox.src} title={lightbox.title} onClose={() => setLightbox(null)} />}
        </View>
    );
};

export default MaterialDeliveryCards;
